import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, X, Check, MessageSquare, Calendar, ArrowRightLeft, GraduationCap, Clock, CheckCheck } from 'lucide-react';

import API from '../utils/api';

var TYPE_CONFIG = {
  message: { icon: MessageSquare, color: '#2D2560', label: 'Message' },
  change_request: { icon: ArrowRightLeft, color: '#10B981', label: 'Change Request' },
  change_request_response: { icon: ArrowRightLeft, color: '#5A4E9E', label: 'Response' },
  event_reminder: { icon: Calendar, color: '#F97316', label: 'Event' },
  exchange_reminder: { icon: Calendar, color: '#EAB308', label: 'Exchange' },
  course_complete: { icon: GraduationCap, color: '#A855F7', label: 'Course' },
};

export default function NotificationBell({ onNavigate, wsNotification }) {
  var [notifications, setNotifications] = useState([]);
  var [unread, setUnread] = useState(0);
  var [open, setOpen] = useState(false);
  var panelRef = useRef(null);

  function load() {
    axios.get(API + '/notifications', { withCredentials: true })
      .then(function (r) { setNotifications(r.data || []); })
      .catch(function () {});
    axios.get(API + '/notifications/unread-count', { withCredentials: true })
      .then(function (r) { setUnread(r.data.count || 0); })
      .catch(function () {});
  }

  useEffect(function () { load(); }, []);

  // Handle real-time notifications from WebSocket
  useEffect(function () {
    if (wsNotification) {
      setNotifications(function (prev) { return [wsNotification].concat(prev).slice(0, 50); });
      setUnread(function (prev) { return prev + 1; });
    }
  }, [wsNotification]);

  // Close on outside click
  useEffect(function () {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return function () { document.removeEventListener('mousedown', handleClick); };
  }, [open]);

  function markRead(id) {
    axios.post(API + '/notifications/' + id + '/read', {}, { withCredentials: true }).then(function () {
      setNotifications(function (prev) { return prev.map(function (n) { return n.id === id ? Object.assign({}, n, { read: true }) : n; }); });
      setUnread(function (prev) { return Math.max(0, prev - 1); });
    });
  }

  function markAllRead() {
    axios.post(API + '/notifications/read-all', {}, { withCredentials: true }).then(function () {
      setNotifications(function (prev) { return prev.map(function (n) { return Object.assign({}, n, { read: true }); }); });
      setUnread(0);
    });
  }

  function dismiss(id) {
    axios.delete(API + '/notifications/' + id, { withCredentials: true }).then(function () {
      setNotifications(function (prev) { return prev.filter(function (n) { return n.id !== id; }); });
    });
  }

  function handleClick(n) {
    if (!n.read) markRead(n.id);
    if (n.link && onNavigate) { onNavigate(n.link); setOpen(false); }
  }

  function timeAgo(ts) {
    if (!ts) return '';
    var diff = (Date.now() - new Date(ts).getTime()) / 1000;
    if (diff < 60) return 'now';
    if (diff < 3600) return Math.floor(diff / 60) + 'm';
    if (diff < 86400) return Math.floor(diff / 3600) + 'h';
    return Math.floor(diff / 86400) + 'd';
  }

  return (
    <div className="relative" ref={panelRef} data-testid="notification-bell">
      <button onClick={function () { setOpen(!open); if (!open) load(); }}
        className="relative p-2 rounded-lg hover:bg-white/10 transition-colors"
        data-testid="notification-bell-btn">
        <Bell className="w-5 h-5 text-white/60" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] flex items-center justify-center rounded-full bg-[#EF4444] text-white text-xs font-bold px-1" data-testid="notification-badge">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 max-h-[70vh] bg-[#FFFFFF] border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-semibold text-white">Notifications</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-[#5A4E9E] hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                  <CheckCheck className="w-3 h-3" /> Mark all read
                </button>
              )}
              <button onClick={function () { setOpen(false); }} className="text-white/30 hover:text-white/60"><X className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[60vh]">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <Bell className="w-8 h-8 text-white/10 mx-auto mb-2" />
                <p className="text-xs text-white/30">No notifications</p>
              </div>
            ) : (
              notifications.map(function (n) {
                var cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.message;
                var Icon = cfg.icon;
                return (
                  <div key={n.id} onClick={function () { handleClick(n); }}
                    className={'w-full text-left px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors flex items-start gap-3 cursor-pointer ' + (n.read ? 'opacity-60' : '')}
                    data-testid={'notification-' + n.id}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: cfg.color + '20' }}>
                      <Icon className="w-4 h-4" style={{ color: cfg.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-medium text-white truncate">{n.title}</p>
                        {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-[#3B82F6] flex-shrink-0"></span>}
                      </div>
                      <p className="text-xs text-white/40 truncate mt-0.5">{n.body}</p>
                      <p className="text-xs text-white/20 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <div onClick={function (e) { e.stopPropagation(); dismiss(n.id); }}
                      className="text-white/15 hover:text-white/40 flex-shrink-0 p-1 cursor-pointer" data-testid={'dismiss-' + n.id}>
                      <X className="w-3 h-3" />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
